import React from 'react'
import { useFormik } from 'formik'
import { Link, useLocation } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import enUS from 'date-fns/locale/en-US'
import arSA from 'date-fns/locale/ar-SA'
import { Button, Grid, Fade, Paper, Collapse, IconButton } from '@material-ui/core'
import { CreateInvoiceStep1FormValidation } from '../../../../utils/validationSchema'
import { FormLoader } from '../../../Loader/FormLoader'
import { formStyles } from '../FormStyles'
import { useStyles } from '../Styles'
import FormSelectField from '../../../../FormFields/FormSelectField'
import invoicesTypes from '../../../../configurations/invoices-types'
import invoicesSubTypes from '../../../../configurations/invoices-sub-types'
import { LanguageConsumer } from '../../../../context/LanguageContext'
import FormTextField from '../../../../FormFields/FormTextField'
import FormDateTimeField from '../../../../FormFields/FormDateTimeField'
import ArrowForwardIosOutlinedIcon from '@material-ui/icons/ArrowForwardIosOutlined'
import CloseIcon from '@material-ui/icons/Close'
import { ItemsListConsumer, ItemsListProvider } from '../../../../context/ItemsListContext'
import { AuthConsumer } from '../../../../context/AuthContext'
import TableWithFilter from '../../../InvoicesHelpers/TableWithFilter'
import Alert from '@material-ui/lab/Alert'
import { NamedRoutes } from '../../../../routes'
import Typography from '@material-ui/core/Typography'
import ArrowBackIosOutlinedIcon from '@material-ui/icons/ArrowBackIosOutlined'
import SaveInvoiceBtn from '../../SaveInvoiceBtn'
import moment from 'moment'
import SearchIcon from '@material-ui/icons/Search'
import FormDateField from '../../../../FormFields/FormDateField'

const InvoiceCreateStep1 = ({ invoiceData, onSubmit, locale, handleInvoiceSubTypeChange, currentInvoiceDetails, getInvoiceDetails, currentInvoiceLoading, currentInvoiceErrors, handleReferencesChange, hasDrafted, handleSaveInvoiceEntity, savedTemplateData }) => {
  const classes = useStyles()
  const formClasses = formStyles()
  const location = useLocation()
  let creditDebitKeys = ['C', 'D', 'EC', 'ED']

  const invoiceStep1Form = useFormik({
    initialValues: (!!Object.keys(invoiceData?.step1 || {}).length) ? invoiceData.step1 : {
      documentType: location?.state?.createNote?.type || 'I',
      documentTypeVersion: '1.0',
      documentSubType: 'full',
      internalID: '',
      generatedInternalID: '',
      internalIDEdit: false,
      dateTimeIssued: '',
      dateTimeIssuedEdit: false,
      serviceDeliveryDate: '',
      serviceDeliveryDateEdit: false,
      references: [],
      originalReferences: [],
      referencesList: [],
      lastReferences: '',
      referencesListCheck: false
    },
    validateOnBlur: false,
    validateOnChange: true,
    validateOnMount: true,
    validationSchema: CreateInvoiceStep1FormValidation,
    onSubmit: ((values, actions) => {
      onSubmit({
        ...values,
        references: invoicesReferencesList,
        referencesList: invoicesReferencesObjectsList
      }, actions, 'step1', locale)
    })
  })

  const [lastDocumentType, setLastDocumentType] = React.useState('')

  const [dateTimeIssued, setDateTimeIssued] = React.useState(invoiceData?.step1?.dateTimeIssued ? new Date(invoiceData?.step1?.dateTimeIssued) : Date())
  const [minDateTimeIssued, setMinDateTimeIssued] = React.useState(null)

  const [serviceDeliveryDate, setServiceDeliveryDate] = React.useState(invoiceData?.step1?.serviceDeliveryDate ? new Date(invoiceData?.step1?.serviceDeliveryDate) : null)
  const [serviceDeliveryDateEdit, setServiceDeliveryDateEdit] = React.useState(invoiceStep1Form.values.serviceDeliveryDateEdit || !['EI', 'EC', 'ED'].includes(invoiceStep1Form.values.documentType))

  const [internalIDEdit, setInternalIDEdit] = React.useState(invoiceStep1Form.values.internalIDEdit)
  const [dateTimeIssuedEdit, setDateTimeIssuedEdit] = React.useState(invoiceStep1Form.values.dateTimeIssuedEdit)

  const [lastReference, setLastReference] = React.useState(location?.state?.createNote?.reference || '')
  const [lastReferenceBack, setLastReferenceBack] = React.useState('')
  const [showReferenceAlert, setShowReferenceAlert] = React.useState(false)
  const [validReference, setValidReference] = React.useState(false)


  const [invoicesReferencesList, setInvoicesReferencesList] = React.useState(invoiceData?.step1?.references || [])
  const [invoicesReferencesObjectsList, setInvoicesReferencesObjectsList] = React.useState(invoiceData?.step1?.referencesList || [])

  const handleSwitchEditChange = (value, setValue) => {
    setValue(value)
  }
  React.useEffect(() => {
      if (!internalIDEdit) {
        invoiceStep1Form.setFieldValue('internalID', invoiceStep1Form.values.generatedInternalID)
      }
      invoiceStep1Form.setFieldValue('internalIDEdit', internalIDEdit || false)
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [internalIDEdit])


  React.useEffect(() => {
      if (!!Object.keys(currentInvoiceDetails || {}).length && (!!lastReferenceBack || !!hasDrafted)) {
        if (!!invoicesReferencesObjectsList?.length) {
          let first = invoicesReferencesObjectsList[0]
          if (!invoicesReferencesObjectsList.map(invoice => invoice?.uuid)?.includes(currentInvoiceDetails?.uuid)) {
            if (currentInvoiceDetails?.receiver?.id === first?.receiver?.id
              && currentInvoiceDetails?.receiver?.name === first?.receiver?.name
              && currentInvoiceDetails?.receiver?.type === first?.receiver?.type
              && currentInvoiceDetails?.taxpayerActivityCode === first?.taxpayerActivityCode
              && currentInvoiceDetails?.purchaseOrderReference === first?.purchaseOrderReference
              && !currentInvoiceDetails?.cancelRequestDelayedDate) {
              setInvoicesReferencesList([...(invoicesReferencesList || []), currentInvoiceDetails])
              setInvoicesReferencesObjectsList([...(invoicesReferencesObjectsList || []), currentInvoiceDetails])
              invoiceStep1Form.setFieldValue('lastReferences', '')
            } else {
              setShowReferenceAlert(true)
            }
          }
        } else {
          setInvoicesReferencesList([...(invoicesReferencesList || []), currentInvoiceDetails])
          setInvoicesReferencesObjectsList([...(invoicesReferencesObjectsList || []), currentInvoiceDetails])
          invoiceStep1Form.setFieldValue('lastReferences', '')
        }
      }
      setLastReferenceBack('')
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentInvoiceDetails])

  React.useEffect(() => {
      if (!!invoicesReferencesList?.length) {
        invoiceStep1Form.setFieldTouched('referencesListCheck', true, true)
        setMinDateTimeIssued(moment.min(invoicesReferencesList?.filter(item => !!item?.dateTimeReceived)?.map(item => moment(item?.dateTimeReceived))))
      } else {
        setMinDateTimeIssued(null)
      }
      invoiceStep1Form.setFieldValue('referencesListCheck', !!invoicesReferencesList?.length)
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [invoicesReferencesList])


  const handleInvoiceTypeChange = (type) => {
    if (['I', 'EI'].includes(type)
      || (['C', 'D'].includes(type) && !['C', 'D'].includes(lastDocumentType))
      || (['EC', 'ED'].includes(type) && !['EC', 'ED'].includes(lastDocumentType))) {
      setLastReference('')
      setLastReferenceBack('')
      setInvoicesReferencesList([])
      setInvoicesReferencesObjectsList([])
      setShowReferenceAlert(false)
      handleReferencesChange(false)
    }
    if (['EI', 'EC', 'ED'].includes(type)) {
      if (!invoiceStep1Form.values.serviceDeliveryDate) {
        setServiceDeliveryDateEdit(false)
        setServiceDeliveryDate(new Date())
        setTimeout(() => invoiceStep1Form.setFieldTouched('serviceDeliveryDate', true, true), 50)
      }
    } else {
      setServiceDeliveryDateEdit(true)
    }
    invoiceStep1Form.setFieldValue('documentSubType', creditDebitKeys.includes(type) ? 'simple' : 'full')
    handleInvoiceSubTypeChange(creditDebitKeys.includes(type) ? 'simple' : 'full')
    setLastDocumentType(type)
  }
  const updateReferences = (references) => {
    if (!!references?.length) {
      let invoicesObjects = invoicesReferencesObjectsList
      let uuids = references?.map(invoice => invoice.uuid)
      invoicesReferencesObjectsList.forEach((invoice, index) => {
        if (!uuids.includes(invoice?.uuid)) {
          invoicesObjects.splice(index, 1)
        }
      })
      setInvoicesReferencesObjectsList(invoicesObjects)
      handleReferencesChange(true)
    } else {
      setInvoicesReferencesObjectsList([])
      setInvoicesReferencesList([])
      setLastReference('')
      handleReferencesChange(false)
    }
  }


  React.useEffect(() => {
      if (!!lastReference) {
        let newData = invoicesReferencesList || []
        if (!newData.map(invoice => invoice?.uuid)?.includes(lastReference)) {
          setLastReferenceBack(lastReference)
          getInvoiceDetails(lastReference, ['EC', 'ED'].includes(invoiceStep1Form.values.documentType))
        }
        if (!!location?.state?.createNote?.reference) {
          window.history.replaceState({}, document.title)
        }
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastReference])

  React.useEffect(() => {
      if (!!currentInvoiceErrors?.length) {
        invoiceStep1Form.setFieldTouched('lastReferences', true, false)
        invoiceStep1Form.setFieldError('lastReferences', currentInvoiceErrors?.join('\n'))
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentInvoiceErrors])

  const handelReferenceChange = (event) => {
    event?.preventDefault()
    event?.stopPropagation()
    setLastReference(invoiceStep1Form.values.lastReferences)
  }

  const handleReferenceField = (event) => {
    invoiceStep1Form.handleChange(event)
    const re = /^[A-Z0-9]{25,30}$/
    setValidReference(re.test(event.target.value))
  }

  React.useEffect(() => {
      if (!dateTimeIssuedEdit) {
        setDateTimeIssued(new Date())
      }
      invoiceStep1Form.setFieldValue('dateTimeIssuedEdit', dateTimeIssuedEdit || false)
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateTimeIssuedEdit])

  const handleDateChange = (date, setDate) => {
    setDate(date)
  }
  React.useEffect(() => {
      if (!!dateTimeIssued) {
        invoiceStep1Form.setFieldValue('dateTimeIssued', dateTimeIssued || '')
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateTimeIssued])

  React.useEffect(() => {
      if (!serviceDeliveryDateEdit) {
        setServiceDeliveryDate(new Date())
      }
      invoiceStep1Form.setFieldValue('serviceDeliveryDateEdit', serviceDeliveryDateEdit || false)
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [serviceDeliveryDateEdit])

  React.useEffect(() => {
      // if (!!serviceDeliveryDate) {
      invoiceStep1Form.setFieldValue('serviceDeliveryDate', serviceDeliveryDate || '')
      // }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [serviceDeliveryDate])


  React.useEffect(() => {
      if (!!hasDrafted && !!Object.keys(invoiceData?.step1 || {}).length) {
        setInternalIDEdit(invoiceData?.step1?.internalIDEdit)
        setDateTimeIssuedEdit(invoiceData?.step1?.dateTimeIssuedEdit)
        setServiceDeliveryDateEdit(invoiceData?.step1?.serviceDeliveryDateEdit)
        invoiceStep1Form.setValues(invoiceData?.step1)
      }

      if (!invoiceData?.step1?.generatedInternalID) {
        let internalId = Math.floor(100000000000 + Math.random() * 900000000000)
        if (!hasDrafted) {
          invoiceStep1Form.setFieldValue('internalID', internalId)
        }
        invoiceStep1Form.setFieldValue('generatedInternalID', internalId)
      }
      let dateIssued = invoiceData?.step1?.dateTimeIssued ? new Date(invoiceData?.step1?.dateTimeIssued) : Date()
      setDateTimeIssued(dateIssued)
      if (invoiceData?.step1?.serviceDeliveryDate || ['EI', 'EC', 'ED'].includes(invoiceStep1Form.values.documentType)) {
        let serviceDate = invoiceData?.step1?.serviceDeliveryDate ? new Date(invoiceData?.step1?.serviceDeliveryDate) : new Date()
        setServiceDeliveryDate(serviceDate)
        // setTimeout(() => invoiceStep1Form.setFieldTouched('serviceDeliveryDate', true, true), 100)
      }
      setTimeout(() => invoiceStep1Form.setFieldTouched('internalID', true, true))
      setLastDocumentType(invoiceData?.step1?.documentType || '')
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [invoiceData])
  return (
    <LanguageConsumer>
      {({ locale }) => (
        <Fade in={true}>
          <Paper elevation={4} className={classes.invoiceStepWrapper}>
            <Grid item xs={12} sm={10} lg={8}>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale === 'ar' ? arSA : enUS}>
                <form onSubmit={invoiceStep1Form.handleSubmit} noValidate autoComplete="off"
                      className={formClasses.formWrapper}>

                  <FormSelectField name={'documentType'} labelKey={'Fields.label.documentType'}
                                   classes={formClasses} setFilter={handleInvoiceTypeChange}
                                   formActions={invoiceStep1Form} required list={invoicesTypes} locale={locale}/>

                  <FormSelectField name={'documentTypeVersion'} labelKey={'Fields.label.documentTypeVersion'}
                                   classes={formClasses}
                                   disabled={creditDebitKeys.includes(invoiceStep1Form.values.documentType)}
                                   formActions={invoiceStep1Form} required type={'documentTypeVersion'}/>

                  <FormSelectField name={'documentSubType'} labelKey={'Fields.label.documentSubType'}
                                   classes={formClasses} setFilter={handleInvoiceSubTypeChange}
                                   formActions={invoiceStep1Form} required row list={invoicesSubTypes}
                                   locale={locale}
                                   disabled={creditDebitKeys.includes(invoiceStep1Form.values.documentType)}/>

                  <FormTextField name={'internalID'}
                                 labelKey={'Fields.label.internalID'} classes={formClasses}
                                 switchEdit switchEditValue={internalIDEdit}
                                 handleSwitchEdit={(value) => handleSwitchEditChange(value, setInternalIDEdit)}
                                 formActions={invoiceStep1Form} required info
                                 infoKey={'Fields.label.internalID.info'}/>

                  <FormDateTimeField name={'dateTimeIssued'} labelKey={'Fields.label.dateTimeIssued'}
                                     classes={formClasses} info infoKey={'Fields.label.dateTimeIssued.info'}
                                     switchEdit switchEditValue={dateTimeIssuedEdit}
                                     handleSwitchEdit={(value) => handleSwitchEditChange(value, setDateTimeIssuedEdit)}
                                     formActions={invoiceStep1Form} required value={dateTimeIssued} disableFuture
                                     atts={!!minDateTimeIssued ? { minDate: minDateTimeIssued } : {}}
                                     handleChange={(date) => handleDateChange(date, setDateTimeIssued)}/>

                  {/*{['EI', 'EC', 'ED'].includes(invoiceStep1Form?.values?.documentType) &&*/}
                  <FormDateField name={'serviceDeliveryDate'} labelKey={'Fields.label.serviceDeliveryDate'}
                                 classes={formClasses} info infoKey={'Fields.label.dateTimeIssued.info'}
                                 switchEdit switchEditValue={serviceDeliveryDateEdit}
                                 handleSwitchEdit={(value) => handleSwitchEditChange(value, setServiceDeliveryDateEdit)}
                                 formActions={invoiceStep1Form} value={serviceDeliveryDate} disableFuture
                                 required={['EI', 'EC', 'ED'].includes(invoiceStep1Form.values.documentType)}
                                 handleChange={(date) => handleDateChange(date, setServiceDeliveryDate)}/>
                  {/*}*/}


                  {creditDebitKeys.includes(invoiceStep1Form.values.documentType) && <>
                    <Collapse in={showReferenceAlert}>
                      <Alert severity="error"
                             action={
                               <IconButton
                                 aria-label="close"
                                 color="inherit"
                                 size="small"
                                 onClick={() => {
                                   setShowReferenceAlert(false)
                                 }}
                               >
                                 <CloseIcon fontSize="inherit"/>
                               </IconButton>
                             }
                      >
                        <FormattedMessage id='InvoicesReference.AlertError'/>
                      </Alert>
                    </Collapse>

                    <div className={classes.referenceWrapper}>
                      <FormTextField name={'lastReferences'} row classes={formClasses}
                                     placeholderKey={'Fields.placeholder.references'}
                                     labelKey={'Fields.label.references'}
                                     hasLoading={currentInvoiceLoading}
                                     hasClear={!!invoiceStep1Form.values.lastReferences}
                                     disabled={currentInvoiceLoading}
                                     formActions={invoiceStep1Form}
                                     required={['EC', 'ED'].includes(invoiceStep1Form.values.documentType)}
                                     onChange={handleReferenceField}
                                     specialError={(invoiceStep1Form.errors.referencesListCheck && invoiceStep1Form.touched.referencesListCheck)
                                     && !(invoiceStep1Form.errors.lastReferences && invoiceStep1Form.touched.lastReferences)
                                     && invoiceStep1Form.errors.referencesListCheck}/>
                      <Button variant={'contained'} size={'large'} color={'primary'}
                              onClick={handelReferenceChange} startIcon={<SearchIcon/>}
                              disabled={!validReference || !!currentInvoiceLoading}>
                        <FormattedMessage id='invoiceSteps.button.reference'/>
                      </Button>
                    </div>


                    <div className={formClasses.fullWidthHint}>
                      <Typography variant={'body1'}><FormattedMessage id='InvoicesReference.hint' values={{
                        strong: chunks => <strong>{chunks}</strong>,
                        invoicesLink: chunks => <Button size={'small'} variant={'text'} color={'primary'}
                                                        component={Link} to={NamedRoutes.invoices}
                                                        className={formClasses.linkBtn} disableRipple
                                                        disableElevation>
                          {chunks}
                        </Button>
                      }}/></Typography>
                    </div>
                    {!!invoicesReferencesList?.length &&
                    <ItemsListProvider entityName={'invoices'} listArr={invoicesReferencesList} all locale={locale}
                                       onListUpdate={(value) => updateReferences(value)}>
                      <ItemsListConsumer>
                        {({ listingData, listingCount, listingRowsPerPage, listingPage, handleChangeListingPage, handleChangeListingRowsPerPage, openDialog }) => (
                          <AuthConsumer>
                            {({ taxpayerData }) => (
                              <TableWithFilter
                                listingData={listingData}
                                listingCount={listingCount}
                                listingRowsPerPage={listingRowsPerPage}
                                listingPage={listingPage}
                                handleChangeListingPage={handleChangeListingPage}
                                handleChangeListingRowsPerPage={handleChangeListingRowsPerPage}
                                taxpayerData={taxpayerData}
                                locale={locale}
                                handleDeleteAction={openDialog}
                                noFilter
                                noPagination
                                onlyDeleteOption
                                noTypeFilter
                                references
                                tabEntityName={'eInvoice'}
                              />
                            )}
                          </AuthConsumer>

                        )}
                      </ItemsListConsumer>
                    </ItemsListProvider>
                    }
                  </>}


                  <div className={formClasses.FormSubmitRow}>
                    {!!hasDrafted &&
                    <SaveInvoiceBtn disabled={!!invoiceStep1Form.isSubmitting || !invoiceStep1Form.isValid}
                                    savedTemplateData={savedTemplateData}
                                    handleSaveInvoiceEntity={(e, entityType, tempData, formikActions, closeDialog) => handleSaveInvoiceEntity(e, entityType, {
                                      ...invoiceStep1Form.values,
                                      references: invoicesReferencesList,
                                      referencesList: invoicesReferencesObjectsList
                                    }, 'step1', tempData, formikActions, closeDialog)}/>
                    }
                    <Button type={'submit'} variant={'contained'} size={'large'} color={'secondary'}
                            onClick={invoiceStep1Form.handleSubmit}
                            disabled={!!invoiceStep1Form.isSubmitting || !invoiceStep1Form.isValid}
                    >
                      <FormattedMessage id='invoiceSteps.button.submit'/>
                      {locale === 'ar' ? <ArrowBackIosOutlinedIcon/> : <ArrowForwardIosOutlinedIcon/>}
                    </Button>
                  </div>
                  {!!invoiceStep1Form.isSubmitting && <FormLoader loading={true}/>}
                </form>
              </MuiPickersUtilsProvider>
            </Grid>
          </Paper>
        </Fade>
      )}
    </LanguageConsumer>
  )
}

export default InvoiceCreateStep1
